<script setup lang="ts">
  import type { QuotedPlanVm } from '~/models/quoting/QuotedPlan'
  import type { BenefitDetail } from '~/composables/BenefitHighlights'
  import { BenefitCategory, BenefitSection } from '~/generated/api-clients-generated'

  const props = defineProps<{
    quote: QuotedPlanVm
    id: string
    stacked?: boolean
    expanded?: boolean
    minimal?: boolean
  }>()

  const { content, allHighlightRows, getDetailsForPlan, isPlanBenefitCovered } =
    await BenefitHighlights.use()

  const { getId } = Id.use(props.id)
  const { show } = DialogController.use()
  const { quotingConfig } = QuotingConfig.use()
  const session = SessionStore.use()

  const isGhostCardConfig = computed(() => quotingConfig.value.ghostCard)
  const hasDocsOrDrugs = computed(() => props.quote?.hasDrugs || props.quote?.hasProviders)

  interface PillHighlight {
    id: string
    label: string
    icon: string
    covered: boolean
    details: BenefitDetail[]
  }

  const partBGiveback = computed(() => props.quote.recommendation.partBReduction ?? 0)

  const { hydratePlaceholders } = Cms.use()

  const pbgBenefit = computed((): PillHighlight[] => [
    {
      id: 'partBGiveback',
      label: 'Part B Giveback',
      icon: 'lucide:hand-helping',
      covered: partBGiveback.value > 0,
      details: [
        {
          name: null,
          description: hydratePlaceholders(content.value?.data?.partBGivebackText ?? '', {
            givebackAmount: formatUsd(partBGiveback.value)
          })
        }
      ]
    }
  ])

  const allHighlights = computed((): PillHighlight[] => [
    ...pbgBenefit.value,
    ...allHighlightRows.value.map((row) => ({
      id: row.id,
      label: row.label,
      icon: row.icon,
      covered: isPlanBenefitCovered(props.quote, row),
      details: getDetailsForPlan(props.quote, row)
    }))
  ])

  const { isOpen, selectedId, triggerCardId } = BenefitHighlightsState.use()
  const { track } = Analytics.use()
  const isAnimating = computed(() => triggerCardId.value === props.id)

  let wasOpenBeforeExpanded = false

  onMounted(() => {
    if (props.expanded) {
      wasOpenBeforeExpanded = isOpen.value
      isOpen.value = true
    }
    if (isOpen.value) updateContentHeight()
  })

  onUnmounted(() => {
    if (props.expanded && !wasOpenBeforeExpanded && selectedId.value === null) {
      isOpen.value = false
    }
  })

  const contentRef = ref<HTMLElement | null>(null)
  const contentHeight = ref(0)
  const isHeightInitialized = ref(!isOpen.value)

  const displayedHighlights = computed(() => {
    const covered = allHighlights.value.filter((h) => h.covered)
    if (props.expanded) return covered
    return selectedId.value ? covered.filter((h) => h.id === selectedId.value) : covered
  })

  const updateContentHeight = () => {
    nextTick(() => {
      requestAnimationFrame(() => {
        if (contentRef.value) {
          contentHeight.value = contentRef.value.scrollHeight
          isHeightInitialized.value = true
        }
      })
    })
  }

  watch([selectedId, isOpen], () => {
    if (isOpen.value) updateContentHeight()
  })

  const setTrigger = () => {
    triggerCardId.value = props.id
    setTimeout(() => {
      triggerCardId.value = null
    }, 200)
  }

  const handleExpandAll = (e: MouseEvent) => {
    setTrigger()

    const button = e.currentTarget as HTMLElement
    const targetTop = button.getBoundingClientRect().top

    const isOpening = !isOpen.value || selectedId.value !== null
    track('BenefitHighlightsExpandAllClicked', { action: isOpening ? 'open' : 'close' })

    if (!isOpen.value) {
      isOpen.value = true
      selectedId.value = null
    } else if (selectedId.value !== null) {
      selectedId.value = null
    } else {
      isOpen.value = false
    }

    nextTick(() => {
      nextTick(() => {
        requestAnimationFrame(() => {
          const shift = Math.round(button.getBoundingClientRect().top - targetTop)
          if (shift !== 0) window.scrollBy({ top: shift, behavior: 'instant' })
        })
      })
    })
  }

  const handlePillClick = (e: MouseEvent, id: string, label: string) => {
    e.stopPropagation()

    // Only trigger card gets drawer animation (eliminating layout-shift)
    setTrigger()

    const button = e.currentTarget as HTMLElement
    const targetTop = button.getBoundingClientRect().top

    const isClosing = selectedId.value === id
    track('BenefitHighlightsButtonClicked', {
      benefit: label,
      action: isClosing ? 'close' : 'open'
    })

    if (isClosing) {
      isOpen.value = false
      selectedId.value = null
    } else {
      isOpen.value = true
      selectedId.value = id
    }

    // Correction RAF queued after all height-update RAFs (nextTick × 2 → RAF)
    nextTick(() => {
      nextTick(() => {
        requestAnimationFrame(() => {
          const shift = Math.round(button.getBoundingClientRect().top - targetTop)
          if (shift !== 0) window.scrollBy({ top: shift, behavior: 'instant' })
        })
      })
    })
  }

  const { visuallyClamped: visuallyClampedHighlights, clampDetectionRef: benefitDescriptionRef } =
    BenefitHighlights.useClampDetection<string>()
</script>

<template>
  <section
    aria-label="Quick View Highlights"
    class="flex flex-col gap-6 rounded-[1.5rem] border px-3.5 py-6"
    :class="[
      expanded ? '' : 'mx-5 w-full',
      { '!mx-1 !px-1': minimal },
      !hasDocsOrDrugs && isGhostCardConfig && !session.ghostCardOptOut
        ? 'border-border-light bg-gray-200/40'
        : 'border-success/15 bg-success-light/60',
      { 'border-none !bg-transparent !pt-3 pb-0': minimal }
    ]"
  >
    <header v-if="!minimal">
      <div
        class="flex gap-5 px-5"
        :class="stacked ? 'justify-center' : 'justify-center sm:justify-between'"
      >
        <h2
          class="flex flex-col items-center gap-1.5 text-center text-2xl font-bold text-primary-900 sm:flex-row sm:items-start sm:text-left sm:leading-[1.1]"
        >
          <HpIcon name="lucide:sparkles" class="mt-1 size-6 shrink-0 text-success-dark" />
          {{ content?.data?.heading }}
        </h2>

        <button
          v-if="!expanded"
          type="button"
          class="shrink-0 items-center gap-1.5 whitespace-nowrap text-base font-semibold text-primary-900"
          :class="stacked ? 'hidden' : 'hidden sm:flex'"
          :aria-expanded="isOpen && selectedId === null"
          @click="handleExpandAll($event)"
        >
          {{ content?.data?.expandText }}
          <HpIcon
            name="lucide:chevron-down"
            class="size-6 transition-transform duration-200"
            :class="{ 'rotate-180': isOpen && selectedId === null }"
          />
        </button>
      </div>

      <p
        class="mt-1.5 text-balance text-lg leading-tight text-gray-900"
        :class="stacked ? 'text-center' : 'pl-1 text-center sm:ml-12 sm:text-left'"
      >
        {{ expanded ? content?.data?.textDetailsPage : content?.data?.text }}
      </p>
    </header>

    <ul
      v-if="!expanded"
      :class="[
        'justify-center gap-1.5',
        { 'flex flex-col xs:flex-row xs:flex-wrap': !minimal },
        { 'flex flex-row flex-wrap': minimal }
      ]"
      role="list"
    >
      <li v-for="highlight in allHighlights" :key="highlight.id" class="flex">
        <button
          type="button"
          class="subtle-shadow flex flex-1 items-center justify-center gap-1.5 overflow-hidden rounded-full border py-1.5 pl-1.5 pr-3 transition-all duration-100"
          :class="
            !highlight.covered
              ? 'cursor-not-allowed border-gray-400 bg-white !shadow-none'
              : selectedId === highlight.id
              ? 'shadow-sm border bg-white outline-2 outline-offset-0 outline-success'
              : 'border-[#abd7a7] bg-white/70 hover:border-success/50 hover:bg-white'
          "
          :disabled="!highlight.covered"
          :title="
            !highlight.covered
              ? `${highlight.label} Not Covered`
              : `View ${highlight.label} Benefits`
          "
          :aria-pressed="selectedId === highlight.id"
          @click="handlePillClick($event, highlight.id, highlight.label)"
        >
          <span
            class="flex shrink-0 items-center justify-center rounded-full border border-border p-1.5"
            :class="highlight.covered ? 'bg-white' : 'bg-gray-100'"
          >
            <HpIcon
              :name="highlight.icon"
              class="size-5"
              :class="highlight.covered ? 'text-success-dark' : 'text-gray-500'"
            />
          </span>
          <span
            class="whitespace-nowrap text-lg font-semibold"
            :class="highlight.covered ? 'text-primary-900' : 'text-gray-500 line-through'"
            >{{ highlight.label }}</span
          >
        </button>
      </li>
    </ul>

    <div
      class="overflow-hidden"
      :class="isAnimating ? 'transition-all duration-150 ease-in-out' : ''"
      :style="
        expanded
          ? {}
          : {
              height: isOpen ? (isHeightInitialized ? `${contentHeight}px` : 'auto') : '0px',
              marginBottom: isOpen ? `0px` : '-1.25rem',
              opacity: isOpen ? 1 : 0
            }
      "
    >
      <div ref="contentRef" class="grid gap-4">
        <div
          v-for="highlight in displayedHighlights"
          :key="highlight.id"
          class="flex flex-col items-start gap-3 rounded-[2rem] bg-white/90 px-6 py-5 xs:flex-row"
        >
          <div
            class="flex size-9 shrink-0 items-center justify-center rounded-full border border-border bg-white p-1.5"
          >
            <HpIcon :name="highlight.icon" class="size-5 text-success-dark" />
          </div>
          <div class="flex flex-col gap-2">
            <h3 class="text-lg font-semibold text-primary-900">
              {{ highlight.label }}
            </h3>
            <template v-if="highlight.details.length">
              <div
                :ref="(el) => benefitDescriptionRef(el, highlight.id)"
                class="flex max-h-[calc(4*1lh)] flex-col gap-1 overflow-hidden"
                :class="
                  visuallyClampedHighlights[highlight.id]
                    ? '[mask-image:linear-gradient(to_bottom,black_calc(100%_-_1.5lh)_85%,transparent)]'
                    : ''
                "
              >
                <template v-for="detail in highlight.details" :key="detail.categoryId">
                  <p v-if="detail.name" class="text-lg font-semibold text-primary-900">
                    {{ detail.name }}
                  </p>
                  <div
                    class="text-lg leading-normal text-gray-800"
                    v-html="detail.description"
                  ></div>
                </template>
              </div>
              <HpAnchor
                v-if="highlight.details.length > 1 || visuallyClampedHighlights[highlight.id]"
                class="self-start text-base font-semibold underline-offset-2"
                @click.prevent="show(getId(`${highlight.id}-see-more-sidebar`))"
              >
                See more &rarr;
              </HpAnchor>
            </template>
          </div>
        </div>
      </div>
    </div>
    <QuoteCardBenefitDetailsSidebar
      v-for="highlight in allHighlights"
      :key="`${highlight.id}-sidebar`"
      :id="getId(`${highlight.id}-see-more-sidebar`)"
      :icon="highlight.icon"
      :label="highlight.label"
      :details="highlight.details"
    />
  </section>
</template>

<style scoped>
  .subtle-shadow {
    box-shadow: 0 2px 3px 0 rgba(111, 114, 133, 0.15);
  }
</style>
